import * as Sentry from '@sentry/react';

import webView from 'common/webView';

import { EXCLUDED_DOMAINS_OR_IP_FROM_SENTRY_BREADCRUMBS } from './constants';
import {
  DestinationPage,
  webAppOpeningTransaction,
} from './webAppOpeningTransaction';

/**
 * Initializes Sentry SDK to track errors and performance monitoring
 */
const initSentry = () => {
  /**
   * @important This is required even if Sentry isn't initialized
   * because we start Sentry transactions in `webAppOpeningTransaction` even when we don't actually send them.
   */
  Sentry.addTracingExtensions();

  if (
    import.meta.env.MODE === 'development' &&
    !import.meta.env.VITE_DEV_ENABLE_SENTRY
  ) {
    return;
  }

  Sentry.init({
    beforeBreadcrumb: (breadcrumb) => {
      // Filter useless breadcrumbs out to decrease noise

      // Network calls breadcrumbs
      if (breadcrumb.type === 'http') {
        const breadcrumbUrl: string | undefined = breadcrumb.data?.url;

        if (!breadcrumbUrl) {
          return breadcrumb;
        }

        const isCallToExcludedDomainOrIp =
          EXCLUDED_DOMAINS_OR_IP_FROM_SENTRY_BREADCRUMBS.some(
            (excludedDomainOrIp) => breadcrumbUrl.includes(excludedDomainOrIp),
          );

        // We will manually create breadcrumbs for GraphQL calls, including the
        // query name for better readability.
        const isCallToGraphql = breadcrumbUrl.includes('/v2/graphql');

        if (isCallToExcludedDomainOrIp || isCallToGraphql) {
          return null;
        }
      }

      // ui.click & ui.input breadcrumbs
      if (
        breadcrumb.category === 'ui.click' ||
        breadcrumb.category === 'ui.input'
      ) {
        const breadcrumbMessage = breadcrumb.message;
        if (!breadcrumbMessage) {
          return breadcrumb;
        }

        // Click on not identified elements
        const identifiedElementSubstrings = ['id=', '#'];
        if (
          !identifiedElementSubstrings.some((identifier) =>
            breadcrumbMessage.includes(identifier),
          )
        ) {
          return null;
        }
      }

      return breadcrumb;
    },

    beforeSend(event) {
      const isApolloError =
        !event.message &&
        event.exception?.values?.some(
          (v) =>
            v.value?.includes('Response not successful: Received status code'),
        );

      if (isApolloError) {
        // Remove errors sent by Apollo as we manually report them in clients.ts
        return null;
      }

      return event;
    },

    beforeSendTransaction(event) {
      /**
       * These are outliers and mostly related to SDK clock drift or some kind of browser tab suspension.
       * We didn't find a better way to handle them.
       */
      if (
        event.timestamp &&
        event.start_timestamp &&
        event.timestamp - event.start_timestamp > 120 // Setting the outlier value to 2 minutes.
      ) {
        return null;
      }

      return event;
    },

    /**
     * Don't forward errors coming from third-party scripts.
     * They are not user-impacting and are coming from GTM scripts in tagmanager.google.com.
     */
    denyUrls: [/www\.googletagmanager\.com/i],

    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_API_ENV,
    release: import.meta.env.VITE_GIT_SHA,
    tracesSampler(samplingContext) {
      if (
        samplingContext.transactionContext.tags?.destination ===
        ('login' satisfies DestinationPage)
      ) {
        return 0.05;
      }

      if (
        samplingContext.transactionContext.name ===
        webAppOpeningTransaction.postLoginTransactionName
      ) {
        return 0.05;
      }

      return 1;
    },
  });

  Sentry.setTag('isInWebView', webView.isInWebView);
};

export default initSentry;
